// Clients: a list, a short form, and archiving. Not a CRM, on purpose.
//
// Round 45 (Nathan): the ABN in the table, Export and Import beside Add
// client, the count under the heading the way Zanetto Tools has it, and the
// archived clients as their own list on a tab instead of a tick box that
// blurred them into the live ones.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { scheduling, type Client } from '../schedulingApi';
import { ApiError } from '../api';
import { Banner, Field } from '../ui';
import { EmptyState, PageHeader } from '../components/ui/Page';
import { CardList, RowCard } from '../components/ui/RowCard';
import { LinkedRow } from '../components/ui/LinkedRow';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';
import { useLatestRequest } from '../lib/useLatestRequest';
import { CsvImport } from '../components/ui/CsvImport';
import { DownloadLink } from '../components/DownloadLink';
import { useOptionalSession } from '../session';

type SortKey = 'name' | 'contact' | 'phone' | 'email' | 'abn';

/**
 * The import template. Its headers are the Export's, so a file exported from
 * Dayboard (or from Zanetto Tools' client list, renamed to these headers)
 * imports straight back. Header names are parse keys (round 37), lower-cased.
 */
const IMPORT_TEMPLATE =
  'Name,Contact,Phone,Email,Address,Accounts contact,Accounts phone,Accounts email,ABN,ACN\n' +
  'Redgum Estates,Sam Patel,0412 345 678,sam@redgum.example,14 Fenwick St Tarneit,Accounts team,03 9000 0000,accounts@redgum.example,51 824 753 556,\n';

/** Old spellings that still import: a ZT export calls it "Client". */
const IMPORT_ALIASES = { client: 'name', 'contact person': 'contact', 'client name': 'name' };

/** "12 clients", "1 client", "3 archived clients": the line under the heading. */
export function clientCountLabel(count: number, archived: boolean): string {
  return `${count} ${archived ? 'archived ' : ''}client${count === 1 ? '' : 's'}`;
}

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [search, setSearch] = useState('');
  // A filter is worth bookmarking, so the tab lives in the URL (round 34).
  const [params, setParams] = useSearchParams();
  const showArchived = params.get('archived') === '1';
  const session = useOptionalSession();
  const isAdmin = session?.user?.role === 'admin';
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const { sortKey, sortDir, onSort } = useSort<SortKey>('name');

  const begin = useLatestRequest();

  const load = useCallback(async () => {
    // Typed-into search: only the newest answer may land. See
    // `useLatestRequest`.
    const isCurrent = begin();
    try {
      const result = await scheduling.listClients({
        search: search || undefined,
        archived: showArchived ? 'only' : false,
      });
      if (!isCurrent()) return;
      setClients(result.clients);
      setLoaded(true);
      setError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : 'Could not load the clients.');
    }
  }, [begin, search, showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  // ONE sorted array, which both the phone cards and the desktop table map
  // over. Sorting inside the table's JSX instead would leave the cards in the
  // server's order, and the two renderings of one list would disagree.
  const visible = useMemo(
    () =>
      sortRows(
        clients,
        (client) => {
          switch (sortKey) {
            case 'name':
              return client.name;
            case 'contact':
              return client.contactPerson;
            case 'phone':
              return client.phone;
            case 'email':
              return client.email;
            case 'abn':
              return client.abn;
          }
        },
        sortDir
      ),
    [clients, sortKey, sortDir]
  );

  async function toggleArchive(client: Client) {
    try {
      await scheduling.archiveClient(client.id, !client.archived);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that.');
    }
  }

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle={loaded ? clientCountLabel(clients.length, showArchived) : undefined}
        actions={
          <>
            {isAdmin && (
              <CsvImport
                entityLabel="client"
                templateName="dayboard-clients-template.csv"
                templateCsv={IMPORT_TEMPLATE}
                requiredHeaders={['name']}
                aliases={IMPORT_ALIASES}
                buttonClassName="btn-secondary"
                previewColumns={[
                  { key: 'name', label: 'Name' },
                  { key: 'contact', label: 'Contact' },
                  { key: 'phone', label: 'Phone' },
                  { key: 'email', label: 'Email' },
                  { key: 'abn', label: 'ABN' },
                ]}
                onImport={async (rows) => {
                  const result = await scheduling.importClients(rows);
                  void load();
                  return result.imported;
                }}
              />
            )}
            <DownloadLink href="/api/clients/export.csv" className="btn-secondary">
              Export CSV
            </DownloadLink>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowForm((open) => !open)}
            >
              {showForm ? 'Close' : 'Add client'}
            </button>
          </>
        }
      />

      {error && <Banner kind="error">{error}</Banner>}

      {showForm && (
        <NewClientForm
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}

      {/* Active and Archived are two lists, never one blurred together
          (round 45). The jobs page's tab idiom. */}
      <div
        className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200"
        role="tablist"
        aria-label="Active or archived clients"
      >
        {(
          [
            [false, 'Active'],
            [true, 'Archived'],
          ] as const
        ).map(([archived, label]) => {
          const active = showArchived === archived;
          return (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (archived) next.set('archived', '1');
                else next.delete('archived');
                setParams(next, { replace: true });
              }}
              className={`min-h-11 whitespace-nowrap border-b-2 px-3 text-sm ${
                active
                  ? 'border-brand-700 font-medium text-brand-800'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-72">
          <label className="form-label" htmlFor="client-search">
            Search
          </label>
          <input
            id="client-search"
            type="search"
            className="form-input"
            placeholder="Search by name, contact or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

      </div>

      {clients.length === 0 ? (
        showArchived ? (
          <EmptyState
            title={search ? 'No archived clients match that' : 'No archived clients'}
            body="A client you archive from the Active list waits here, and can be restored at any time."
          />
        ) : (
          <EmptyState
            title={search ? 'No clients match that' : 'No clients yet'}
            body="Clients are optional; internal work does not need one. They exist to group jobs, not to be a CRM."
          />
        )
      ) : (
        <>
        <CardList>
          {visible.map((client) => (
            <RowCard
              key={client.id}
              title={client.name}
              to={`/app/clients/${client.id}`}
              badges={client.archived && !showArchived ? <span className="pill">archived</span> : undefined}
              actions={
                <>
                  {/* tel: on a phone because this list is read standing on a
                      site — tapping the number is the whole reason it is on
                      the card. It lives in the ACTION row, not the body:
                      the body is inside the card's own link now, and an
                      anchor inside an anchor is invalid HTML that browsers
                      resolve however they like. */}
                  {client.phone && (
                    <a
                      className="row-action"
                      href={`tel:${client.phone.replace(/[^+\d]/g, '')}`}
                    >
                      {client.phone}
                    </a>
                  )}
                  <button
                    type="button"
                    className={client.archived ? 'row-action' : 'row-action-muted'}
                    onClick={() => void toggleArchive(client)}
                  >
                    {client.archived ? 'Restore' : 'Archive'}
                  </button>
                </>
              }
            >
              {client.contactPerson && <div>{client.contactPerson}</div>}
              {client.email && <div className="break-words">{client.email}</div>}
              {client.abn && <div className="table-muted">ABN {client.abn}</div>}
            </RowCard>
          ))}
        </CardList>
        <div className="table-wrap max-sm:hidden">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader label="Name" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Contact" sortKey="contact" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Phone" sortKey="phone" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="Email" sortKey="email" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortHeader label="ABN" sortKey="abn" current={sortKey} dir={sortDir} onSort={onSort} />
                {/* w-px keeps the action column exactly as wide as its button
                    instead of letting it take an even share of the table. */}
                <th className="w-px" />
              </tr>
            </thead>
            <tbody>
              {visible.map((client) => (
                <LinkedRow key={client.id} to={`/app/clients/${client.id}`}>
                  <td className="font-medium">
                    <Link className="table-link" to={`/app/clients/${client.id}`}>
                      {client.name}
                    </Link>
                    {client.archived && !showArchived && <span className="pill ml-2">archived</span>}
                  </td>
                  <td className="table-muted">{client.contactPerson || '—'}</td>
                  <td className="table-muted whitespace-nowrap">{client.phone || '—'}</td>
                  <td className="table-muted">{client.email || '—'}</td>
                  <td className="table-muted whitespace-nowrap tabular-nums">{client.abn || '—'}</td>
                  <td className="whitespace-nowrap text-right">
                    <Link className="row-action" to={`/app/clients/${client.id}`}>
                      Edit
                    </Link>
                    <button
                      type="button"
                      className={`ml-1 ${client.archived ? 'row-action' : 'row-action-muted'}`}
                      onClick={() => void toggleArchive(client)}
                    >
                      {client.archived ? 'Restore' : 'Archive'}
                    </button>
                  </td>
                </LinkedRow>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      <p className="hint mt-3">
        A client with jobs against it can be archived but not deleted; the jobs are the record of
        work done.
      </p>
    </>
  );
}

function NewClientForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [accountsName, setAccountsName] = useState('');
  const [accountsPhone, setAccountsPhone] = useState('');
  const [accountsEmail, setAccountsEmail] = useState('');
  const [abn, setAbn] = useState('');
  const [acn, setAcn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await scheduling.createClient({
        name,
        contactPerson,
        phone,
        email,
        address,
        accountsName,
        accountsPhone,
        accountsEmail,
        abn,
        acn,
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFields(err.fields);
      } else {
        setError('Could not add that client.');
      }
    } finally {
      setBusy(false);
    }
  }

  // The same fields in the same arrangement as the client page's details form
  // (round 36 — round 34 added ABN/ACN/accounts there and this form was never
  // brought along, so a client entered here immediately needed a second visit
  // to finish). Capped at the same max-w-3xl as the page it feeds; the shell
  // caps nothing, and a five-field form stretched across a wide monitor is
  // all gap.
  return (
    <section className="panel max-w-3xl">
      <h2>Add a client</h2>
      {error && <Banner kind="error">{error}</Banner>}
      <form onSubmit={submit} noValidate>
        <Field label="Name" name="name" value={name} onChange={setName} error={fields.name} />

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contact details
            </h3>
            <Field
              label="Contact person"
              name="contactPerson"
              value={contactPerson}
              onChange={setContactPerson}
              error={fields.contactPerson}
              required={false}
            />
            <Field
              label="Phone"
              name="phone"
              value={phone}
              onChange={setPhone}
              error={fields.phone}
              required={false}
              inputMode="tel"
            />
            <Field
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={setEmail}
              error={fields.email}
              required={false}
              autoCapitalize="none"
              spellCheck={false}
              inputMode="email"
            />
          </div>

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Accounts
            </h3>
            <Field
              label="Accounts contact"
              name="accountsName"
              value={accountsName}
              onChange={setAccountsName}
              error={fields.accountsName}
              required={false}
            />
            <Field
              label="Accounts phone"
              name="accountsPhone"
              value={accountsPhone}
              onChange={setAccountsPhone}
              error={fields.accountsPhone}
              required={false}
              inputMode="tel"
            />
            <Field
              label="Accounts email"
              name="accountsEmail"
              value={accountsEmail}
              onChange={setAccountsEmail}
              error={fields.accountsEmail}
              required={false}
              type="email"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="email"
            />
          </div>
        </div>

        <div className="row">
          <Field
            label="ABN"
            name="abn"
            value={abn}
            onChange={setAbn}
            error={fields.abn}
            required={false}
            inputMode="numeric"
            autoCapitalize="none"
            spellCheck={false}
          />
          <Field
            label="ACN"
            name="acn"
            value={acn}
            onChange={setAcn}
            error={fields.acn}
            required={false}
            inputMode="numeric"
            autoCapitalize="none"
            spellCheck={false}
          />
        </div>

        <Field
          label="Address"
          name="address"
          value={address}
          onChange={setAddress}
          error={fields.address}
          required={false}
        />

        {/* The class is explicit rather than left to legacy.css's bare-button
            rule, because the button needs the top margin: `.row` strips its
            labels' bottom margin, so a submit following a row has nothing
            spacing it from the fields and lands hard on the panel's edge. */}
        <button type="submit" className="btn-primary mt-4" disabled={busy}>
          {busy ? 'Adding…' : 'Add client'}
        </button>
      </form>
    </section>
  );
}
